import {
  View,
  Text,
  TouchableOpacity,
  Image,
  Platform,
  Modal,
  ActivityIndicator,
  Pressable,
} from "react-native";
import React, { useRef, useState } from "react";
import { router } from "expo-router";
import i18n from "../../locales/i18n";
import { Swipeable } from "react-native-gesture-handler";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";

interface ProductItemProps {
  price_sell: number;
  productname: string;
  stock_nbr: number;
  price_bought: number;
  barcode: string;
  photo: string;
  id: number;
  type_quantity?: string;
  onDelete?: (id: number) => void;
}

const ProductItem = ({
  price_sell,
  productname,
  stock_nbr,
  price_bought,
  barcode,
  photo,
  id: productid,
  type_quantity,
  onDelete,
}: ProductItemProps) => {
  const swipeableRef = useRef<Swipeable>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleItemPress = () => {
    // Navigate to add product page with edit mode and product data
    router.push({
      pathname: "/addproduct",
      params: {
        mode: "edit",
        productId: productid,
        productName: productname,
        priceSell: price_sell,
        priceBought: price_bought,
        stock: stock_nbr,
        barcode: barcode || "",
        photo: photo || "",
        typeOfQuantity: type_quantity || "unit",
      },
    });
  };

  const handleDelete = () => {
    swipeableRef.current?.close();
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    await onDelete?.(productid);
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  const renderRightActions = () => {
    if (!onDelete) return null;
    return (
      <TouchableOpacity
        onPress={handleDelete}
        className="bg-red-500 justify-center items-center rounded-xl mb-4 ml-2"
        style={{ width: 80 }}
      >
        <MaterialIcons name="delete" size={28} color="white" />
        <Text className="text-white text-xs font-bold mt-1">
          {i18n.t("delete")}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderLeftActions = () => {
    if (!onDelete) return null;
    return (
      <TouchableOpacity
        onPress={handleDelete}
        className="bg-red-500 justify-center items-center rounded-xl mb-4 mr-2"
        style={{ width: 80 }}
      >
        <MaterialIcons name="delete" size={28} color="white" />
        <Text className="text-white text-xs font-bold mt-1">
          {i18n.t("delete")}
        </Text>
      </TouchableOpacity>
    );
  };

  const deleteModal = (
    <Modal
      visible={showDeleteModal}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowDeleteModal(false)}
    >
      <Pressable
        onPress={() => setShowDeleteModal(false)}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.5)",
          justifyContent: "center",
          alignItems: "center",
          padding: 20,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            backgroundColor: "white",
            borderRadius: 20,
            padding: 28,
            width: "100%",
            maxWidth: 320,
            alignItems: "center",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.25,
            shadowRadius: 24,
            elevation: 20,
          }}
        >
          {/* Warning Icon */}
          <View
            style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              backgroundColor: "#FEE2E2",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <MaterialIcons name="delete" size={32} color="#DC2626" />
          </View>

          <Text
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: "#DC2626",
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            {i18n.t("deleteProduct")}
          </Text>
          <Text
            style={{
              fontSize: 14,
              color: "#666",
              textAlign: "center",
              marginBottom: 6,
              lineHeight: 20,
            }}
          >
            {i18n.t("confirmDelete")}
          </Text>
          <Text
            style={{
              fontSize: 16,
              fontWeight: "600",
              color: "#333",
              marginBottom: 22,
              textAlign: "center",
            }}
          >
            {productname}
          </Text>

          <View style={{ flexDirection: "row", gap: 10, width: "100%" }}>
            <TouchableOpacity
              onPress={() => setShowDeleteModal(false)}
              disabled={isDeleting}
              style={{
                flex: 1,
                paddingVertical: 13,
                borderRadius: 12,
                backgroundColor: "#F3F4F6",
                borderWidth: 1,
                borderColor: "#E5E7EB",
                alignItems: "center",
              }}
            >
              <Text
                style={{ fontSize: 15, fontWeight: "600", color: "#374151" }}
              >
                {i18n.t("cancel")}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={confirmDelete}
              disabled={isDeleting}
              style={{
                flex: 1,
                paddingVertical: 13,
                borderRadius: 12,
                backgroundColor: "#DC2626",
                alignItems: "center",
                opacity: isDeleting ? 0.6 : 1,
              }}
            >
              {isDeleting ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <Text
                  style={{ fontSize: 15, fontWeight: "600", color: "#FFFFFF" }}
                >
                  {i18n.t("delete")}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  const content = (
    <TouchableOpacity onPress={handleItemPress}>
      <View className="w-full bg-white mb-4 gap-2 items-center flex flex-row-reverse justify-between rounded-xl py-4 px-2 border border-gray-200">
        {/* Product Photo */}
        <View className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 items-center justify-center">
          {photo ? (
            <Image
              source={{ uri: photo }}
              className="w-full h-full"
              resizeMode="cover"
            />
          ) : (
            <Text className="text-gray-500 text-xs">{i18n.t("noImage")}</Text>
          )}
        </View>

        {/* Product Name */}
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          className="text-gray-800 font-bold text-sm text-center flex-1 px-1"
        >
          {productname}
        </Text>

        {/* Quantity */}
        <View className="items-center justify-center" style={{ minWidth: 40 }}>
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            className="text-gray-800 font-bold text-sm text-center"
          >
            {Math.floor(stock_nbr)}
          </Text>
        </View>

        {/* Buy Price */}
        <View className="items-center justify-center" style={{ minWidth: 80 }}>
          <Text
            numberOfLines={1}
            className="text-gray-800 font-bold text-center text-sm"
          >
            {price_bought} {i18n.t("currency")}
          </Text>
        </View>

        {/* Total Value */}
        <View className="items-center justify-center" style={{ minWidth: 80 }}>
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            className="text-gray-800 font-bold text-center text-sm"
          >
            {(stock_nbr * price_bought).toFixed(2)} {i18n.t("currency")}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  if (Platform.OS === "web" || !onDelete) {
    return (
      <>
        {content}
        {deleteModal}
      </>
    );
  }

  return (
    <>
      <Swipeable
        ref={swipeableRef}
        renderRightActions={renderRightActions}
        renderLeftActions={renderLeftActions}
        overshootRight={false}
        overshootLeft={false}
      >
        {content}
      </Swipeable>
      {deleteModal}
    </>
  );
};

export default ProductItem;
